import type { FilterFormValues } from '@vlabs/monokit/entities/matcher/types/types';

type EventFilterValues = Partial<Record<keyof FilterFormValues, unknown>> & {
  create_time__lt?: unknown;
  create_time__gte?: unknown;
};

interface EventFilterActivity {
  perFilter: {
    date: boolean;
    sources: boolean;
    handlers: boolean;
    similarity: boolean;
    comparingAttribute: boolean;
    topSimilarObjects: boolean;
    gender: boolean;
    age: boolean;
    emotion: boolean;
    masks: boolean;
    liveness: boolean;
    deepfake: boolean;
    faceIds: boolean;
    apparentGender: boolean;
    apparentAge: boolean;
    upper: boolean;
    lower: boolean;
    shoes: boolean;
    headwear: boolean;
    backpack: boolean;
    location: boolean;
  };
  counts: {
    general: number;
    comparing: number;
    face: number;
    body: number;
  };
}

function hasValue(value: unknown): boolean {
  if (value === undefined || value === null) return false;
  if (Array.isArray(value)) return value.length > 0;
  if (value instanceof Date) return true;
  if (typeof value === 'string') return value.trim() !== '';
  return Boolean(value);
}

function hasSomeValues(values: unknown[]): boolean {
  return values.some(hasValue);
}

function hasLocationValues(filters: EventFilterValues): boolean {
  return [
    filters.areas,
    filters.cities,
    filters.districts,
    filters.streets,
    filters.house_numbers,
    filters.latitude_delta,
    filters.longitude_delta,
    filters.origin_latitude,
    filters.origin_longitude,
  ].some(hasValue);
}

function countApplied(values: boolean[]): number {
  return values.filter(Boolean).length;
}

function getEventFilterActivity(filters: EventFilterValues): EventFilterActivity {
  const perFilter = {
    date: hasSomeValues([filters.create_time__lt, filters.create_time__gte]),
    sources: hasValue(filters.sources),
    handlers: hasValue(filters.handler_ids),
    similarity: hasValue(filters.threshold),
    comparingAttribute: hasValue(filters.top_matching_candidates_label),
    topSimilarObjects: hasValue(filters.top_similar_object_ids),
    gender: hasValue(filters.gender),
    age: hasValue(filters.age),
    emotion: hasValue(filters.emotions),
    masks: hasValue(filters.masks),
    liveness: hasValue(filters.liveness),
    deepfake: hasValue(filters.deepfake),
    faceIds: hasValue(filters.face_ids),
    apparentGender: hasValue(filters.apparent_gender),
    apparentAge: hasValue(filters.apparent_age),
    upper: hasSomeValues([filters.upper_clothing_colors, filters.sleeve_lengths]),
    lower: hasSomeValues([filters.lower_garment_colors, filters.lower_garment_types]),
    shoes: hasValue(filters.shoes_apparent_colors),
    headwear: hasSomeValues([filters.headwear_apparent_colors, filters.headwear_states]),
    backpack: hasValue(filters.backpack_states),
    location: hasLocationValues(filters),
  };

  const counts = {
    general: countApplied([
      perFilter.date,
      perFilter.sources,
      perFilter.handlers,
      perFilter.similarity,
    ]),
    comparing: countApplied([
      perFilter.comparingAttribute,
      perFilter.topSimilarObjects,
    ]),
    face: countApplied([
      perFilter.gender,
      perFilter.age,
      perFilter.emotion,
      perFilter.masks,
      perFilter.liveness,
      perFilter.deepfake,
      perFilter.faceIds,
    ]),
    body: countApplied([
      perFilter.apparentGender,
      perFilter.apparentAge,
      perFilter.upper,
      perFilter.lower,
      perFilter.shoes,
      perFilter.headwear,
      perFilter.backpack,
    ]),
  };

  return { perFilter, counts };
}

export { getEventFilterActivity };
export type { EventFilterActivity, EventFilterValues };
